import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Button } from '@common/Button';
import { Input } from '@common/FormFields';

export const StructuresList = ({ structures = [], vehicleHeight, onSelectStructure }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('clearance'); // 'clearance', 'name', 'distance'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'

  if (!structures.length) {
    return (
      <div className="bg-gray-700/50 p-4 rounded-lg text-center">
        <p className="text-gray-400">No structures found along this route</p>
      </div>
    );
  }

  // Filter structures based on search term
  const filteredStructures = structures.filter(structure => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (structure.name ? structure.name.toLowerCase().includes(searchLower) : false) ||
      structure.properties?.municipality?.toLowerCase().includes(searchLower) ||
      structure.properties?.route?.toLowerCase().includes(searchLower)
    );
  });

  // Sort structures
  const sortedStructures = [...filteredStructures].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'clearance':
        aValue = a.properties?.clearance || 999;
        bValue = b.properties?.clearance || 999;
        break;
      case 'name':
        aValue = a.name ? a.name : '';
        bValue = b.name ? b.name : '';
        break;
      case 'distance':
        aValue = a.distanceFromRoute || 0;
        bValue = b.distanceFromRoute || 0;
        break;
      default:
        aValue = a.properties?.clearance || 999;
        bValue = b.properties?.clearance || 999;
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get status color based on clearance
  const getStatusColor = (clearance) => {
    if (!clearance || !vehicleHeight) return 'text-gray-400';
    
    // vehicleHeight already includes safety buffer from form
    const cautionBuffer = 0.5; // Additional 50cm buffer for caution zone
    
    if (clearance < vehicleHeight) {
      return 'text-red-500'; // Danger
    } else if (clearance < vehicleHeight + cautionBuffer) {
      return 'text-yellow-500'; // Caution
    } else {
      return 'text-green-500'; // Safe
    }
  };

  // Get status icon based on clearance
  const getStatusIcon = (clearance) => {
    if (!clearance || !vehicleHeight) return Info;
    
    // vehicleHeight already includes safety buffer from form
    const cautionBuffer = 0.5; // Additional 50cm buffer for caution zone
    
    if (clearance < vehicleHeight) {
      return AlertTriangle;
    } else if (clearance < vehicleHeight + cautionBuffer) {
      return AlertTriangle;
    } else {
      return CheckCircle;
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-700/50 p-4 rounded-lg">
        <h3 className="text-white font-medium mb-2">
          Structures ({filteredStructures.length})
        </h3>

        {/* Search and Sort */}
        <div className="mb-4">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search structures..."
            className="bg-gray-800 border-gray-600 text-white mb-2"
          />
          
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-gray-800 border-gray-600 text-white rounded p-1 text-sm flex-1"
            >
              <option value="clearance">Sort by Clearance</option>
              <option value="name">Sort by Name</option>
              <option value="distance">Sort by Distance</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="bg-gray-800 border border-gray-600 rounded p-1 text-sm text-white"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Structures List */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
          {sortedStructures.map((structure) => {
            const clearance = structure.properties?.clearance;
            const StatusIcon = getStatusIcon(clearance);
            const statusColor = getStatusColor(clearance);
            
            return (
              <button
                key={structure.id}
                onClick={() => onSelectStructure(structure)}
                className="w-full text-left bg-gray-800 hover:bg-gray-700 p-3 rounded-lg transition-colors"
              >
                <div className="flex items-start gap-2">
                  <StatusIcon className={`w-5 h-5 ${statusColor} shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate"> 
                      {structure.name || `Structure ${structure.id}`}
                    </h4>
                    <div className="text-sm text-gray-400 truncate">
                      {structure.properties?.municipality || ''}
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className={`text-sm font-medium ${statusColor}`}>
                        {clearance ? `${clearance.toFixed(2)} m` : 'Unknown'}
                      </span>
                      {structure.distanceFromRoute && (
                        <span className="text-xs text-gray-400">
                          {structure.distanceFromRoute.toFixed(0)} m from route
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          
          {filteredStructures.length === 0 && (
            <div className="text-center py-4 text-gray-400">
              No structures match your search
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StructuresList;